import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import Select from "../../Form/Select";
import { useSelectorApp } from "../../../lib/redux";
import { logoPlatform } from "../../../utils/string";
import { getUniqueID } from "../../../utils/numbers";
import useButtonActionPlatform from "../../../hooks/useButtonActionPlatform";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_CONNECT_ACCOUNT,
  CURRENT,
} from "../../../i18n/keysTranslations";
import {
  META,
  FACEBOOK,
  TIKTOK,
  TIKTOK_BUSINESS,
  GOOGLE,
  LINKEDIN,
  CANVA,
  YOUTUBE,
} from "../../../utils/constants";

const SELECT_MAX_WIDTH_PX = 280;

/** Sentinel value for the "connect another account" row (same as SelectBusinessAccount). */
const ID_ADD = "Add Account";

const CONNECT_PLATFORMS = new Set([
  META,
  TIKTOK,
  GOOGLE,
  LINKEDIN,
  CANVA,
  YOUTUBE,
]);

/**
 * Returns the integrations key inside `user.integrations` for a given platform.
 */
const getIntegrationsKey = (platform) => {
  switch (platform) {
    case META:
      return FACEBOOK;
    case TIKTOK:
      return TIKTOK_BUSINESS;
    default:
      return platform;
  }
};

/**
 * Checks whether an integration has access to the given adAccountID.
 * For Meta: checks businessAccounts. For others: checks advertisers.
 */
const integrationHasAdAccount = (integration, platform, adAccountID) => {
  if (!adAccountID) return true;

  if (platform === META) {
    return Boolean(integration?.businessAccounts?.[adAccountID]);
  }

  return Boolean(integration?.advertisers?.[adAccountID]);
};

const IntegrationOptionLabel = ({ logoSrc, name, id, secondaryLabel }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      minWidth: 0,
      py: 0.25,
    }}
  >
    {logoSrc ? (
      <Avatar
        src={logoSrc}
        variant="square"
        sx={{
          width: 18,
          height: 18,
          flexShrink: 0,
          "& .MuiAvatar-img": { objectFit: "contain" },
        }}
      />
    ) : null}
    <Box
      sx={{
        minWidth: 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography variant="body2" noWrap component="span" display="block">
        {name || id}
      </Typography>
      {secondaryLabel ? (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          component="span"
          display="block"
          sx={{ lineHeight: 1.2 }}
        >
          {secondaryLabel}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

const ConnectAccountOptionLabel = () => {
  const { t } = useTranslationApp();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <AddIcon
        sx={{
          ml: -0.5,
          fontSize: 20,
          color: "background.paperContrastText",
        }}
      />
      <Typography variant="body2" component="span">
        {t(BUTTON_CONNECT_ACCOUNT)}
      </Typography>
    </Box>
  );
};

/**
 * Integration picker using shared Form/Select + react-hook-form Controller.
 *
 * Props:
 *   control             - RHF control
 *   name                - field name (unique per row)
 *   platform            - "meta" | "google" | "tiktok" | "linkedIn" | …
 *   onlyShowWithThisAdAccountID - filters integrations that include this ad account
 *   disabled            - disables the select
 *   errors              - RHF errors object
 */
const SelectIntegration = ({
  control,
  name,
  platform,
  onlyShowWithThisAdAccountID,
  currentIntegrationID,
  disabled = false,
  errors = {},
}) => {
  const { t } = useTranslationApp();
  const integrations = useSelectorApp((state) => state?.user?.integrations);
  const platformLogoSrc = logoPlatform(platform);
  const BUTTON_ACTIONS_COMPONENTS = useButtonActionPlatform();
  const [hiddenButtonId] = useState(() => getUniqueID());
  const [connectingAccount, setConnectingAccount] = useState(false);

  const hasConnectPlatform = CONNECT_PLATFORMS.has(platform);
  const PlatformConnectButton = hasConnectPlatform
    ? BUTTON_ACTIONS_COMPONENTS[platform]
    : null;

  const { options, integrationCount } = useMemo(() => {
    const platformKey = getIntegrationsKey(platform);
    const platformIntegrations = integrations?.[platformKey] || {};
    const integrationOpts = Object.values(platformIntegrations)
      .filter((integration) =>
        integrationHasAdAccount(
          integration,
          platform,
          onlyShowWithThisAdAccountID
        )
      )
      .map((integration) => ({
        value: integration.id,
        label: (
          <IntegrationOptionLabel
            logoSrc={platformLogoSrc}
            name={integration.name}
            id={integration.id}
            secondaryLabel={
              integration.id === currentIntegrationID ? t(CURRENT) : ""
            }
          />
        ),
      }));

    if (!hasConnectPlatform) {
      return {
        options: integrationOpts,
        integrationCount: integrationOpts.length,
      };
    }

    return {
      options: [
        {
          value: ID_ADD,
          label: <ConnectAccountOptionLabel />,
        },
        ...integrationOpts,
      ],
      integrationCount: integrationOpts.length,
    };
  }, [
    integrations,
    platform,
    onlyShowWithThisAdAccountID,
    platformLogoSrc,
    hasConnectPlatform,
    currentIntegrationID,
    t,
  ]);

  const onClickConnectAccount = async () => {
    setConnectingAccount(true);
    document.getElementById(hiddenButtonId)?.click();
  };

  const renderSelectedValue = (selectedValue) => {
    const selectedIntegration = options.find(
      (option) => option.value === selectedValue
    );

    if (!selectedIntegration) {
      return null;
    }

    const platformKey = getIntegrationsKey(platform);
    const integration = integrations?.[platformKey]?.[selectedValue];

    if (!integration) {
      return selectedIntegration.label;
    }

    return (
      <IntegrationOptionLabel
        logoSrc={platformLogoSrc}
        name={integration.name}
        id={integration.id}
      />
    );
  };

  const validationChange = async (value) => {
    if (value === ID_ADD) {
      await onClickConnectAccount();
      return false;
    }
    return true;
  };

  const selectDisabled =
    disabled ||
    connectingAccount ||
    (integrationCount === 0 && !hasConnectPlatform);

  return (
    <>
      {PlatformConnectButton
        ? React.cloneElement(PlatformConnectButton, {
            id: hiddenButtonId,
            text: BUTTON_CONNECT_ACCOUNT,
            mode: "add",
            icon: <AddIcon />,
            showButtonIcon: true,
            showLogo: false,
            onClick: () => {},
            onCallbackOpenPopup: () => {
              setConnectingAccount(false);
            },
            onCallbackCancel: () => {
              setConnectingAccount(false);
            },
            sx: {
              display: "none",
              justifyContent: "flex-start",
              background: "transparent",
              color: "inherit",
              m: 0,
              ml: -1,
              py: 1,
              px: 2,
              fontSize: "1rem",
              fontWeight: 500,
              "&:hover": {
                background: "transparent",
              },
            },
          })
        : null}
      <Select
        control={control}
        name={name}
        errors={errors}
        options={options}
        rules={{ required: false }}
        showEmptyOption={true}
        displayEmpty={true}
        fullWidth={false}
        variant="filled"
        doTranslate={false}
        disabled={selectDisabled}
        size="small"
        validationChange={validationChange}
        renderValue={renderSelectedValue}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 48 * 4.5 + 8 * 2,
              minWidth: SELECT_MAX_WIDTH_PX,
              maxWidth: SELECT_MAX_WIDTH_PX,
            },
          },
        }}
        sx={{
          maxWidth: SELECT_MAX_WIDTH_PX,
          minWidth: SELECT_MAX_WIDTH_PX,
          width: SELECT_MAX_WIDTH_PX,
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            whiteSpace: "normal",
            py: 0.75,
            minHeight: 48,
          },
        }}
      />
    </>
  );
};

export default SelectIntegration;
